(function (root, factory) {
  'use strict';

  if (typeof module === 'object' && module.exports) {
    module.exports = { createProductCardHost: factory };
    return;
  }

  root.SAProductCard = factory(root);
}(typeof globalThis !== 'undefined' ? globalThis : this, function createProductCardHost(root) {
  'use strict';

  var document = root.document;
  var records = new WeakMap();
  var boundCards = new Set();
  var observer = null;
  var started = false;
  var stopped = false;
  var cartListener = null;
  var cartRequest = 0;

  function normalizeRating(card) {
    var badge = card.querySelector('.jdgm-prev-badge');
    var stars = card.querySelector('.jdgm-prev-badge__stars');
    var score = stars ? parseFloat(stars.getAttribute('data-score')) : 0;
    var countText = badge ? badge.getAttribute('data-number-of-reviews') : '';
    var count = parseInt(String(countText || '').replace(/[^0-9]/g, ''), 10) || 0;
    var previous = String(card.dataset.rating || '') + '|' + String(card.dataset.reviews || '');
    card.dataset.rating = String(score || 0);
    card.dataset.reviews = String(count);
    if (stars && Number.isFinite(score) && score > 0) {
      var rounded = score.toFixed(1);
      stars.setAttribute('data-score-rounded', rounded);
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', rounded + ' out of 5 stars');
    }
    var current = card.dataset.rating + '|' + card.dataset.reviews;
    if (current !== previous && root.CustomEvent && card.dispatchEvent) {
      card.dispatchEvent(new root.CustomEvent('sa:product-card-rating', {
        bubbles: true,
        detail: { card: card }
      }));
    }
  }

  function syncPendingAccessibility(card) {
    var record = records.get(card);
    if (!record || !record.button) return;
    if (record.button.classList.contains('is-pending')) {
      record.button.setAttribute('aria-busy', 'true');
    } else {
      record.button.removeAttribute('aria-busy');
    }
  }

  function closestCard(node) {
    if (!node || node.nodeType !== 1 || !node.closest) return null;
    return node.closest('[data-sa-product-card]');
  }

  function projectCardCartState(card, productIds) {
    var record = records.get(card);
    if (!record || !record.button) return;
    var button = record.button;
    var productId = card.getAttribute('data-product-id');
    var inCart = !!productIds[String(productId)];
    var permanent = button.hasAttribute('data-permanently-disabled');

    card.classList.remove('is-pending');
    button.classList.remove('is-pending');
    card.classList.toggle('is-in-cart', inCart);
    button.classList.toggle('is-in-cart', inCart);
    button.removeAttribute('aria-busy');

    if (permanent) {
      button.setAttribute('data-originally-disabled', '');
    } else {
      button.removeAttribute('data-originally-disabled');
    }

    if (inCart) {
      button.disabled = true;
      button.setAttribute('disabled', '');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-label', record.title + ' is in cart');
    } else if (permanent) {
      button.disabled = true;
      button.setAttribute('disabled', '');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-label', record.originalLabel);
    } else {
      button.disabled = false;
      button.removeAttribute('disabled');
      button.removeAttribute('aria-disabled');
      button.setAttribute('aria-label', record.originalLabel);
    }
  }

  function projectCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    var productIds = Object.create(null);
    cart.items.forEach(function (item) {
      productIds[String(item.product_id)] = true;
    });
    boundCards.forEach(function (card) { projectCardCartState(card, productIds); });
  }

  function syncCart() {
    if (!root.SACart) return;
    var cached = typeof root.SACart.peek === 'function' ? root.SACart.peek() : null;
    if (cached) {
      cartRequest += 1;
      projectCart(cached);
      return;
    }
    if (typeof root.SACart.get !== 'function') return;
    var request = ++cartRequest;
    root.SACart.get().then(function (cart) {
      if (request === cartRequest) projectCart(cart);
    }).catch(function () {});
  }

  function collect(scope) {
    var cards = [];
    if (!scope) return cards;
    if (scope.nodeType === 1 && scope.matches && scope.matches('[data-sa-product-card]')) {
      cards.push(scope);
    }
    if (scope.querySelectorAll) {
      Array.prototype.push.apply(cards, scope.querySelectorAll('[data-sa-product-card]'));
    }
    return cards;
  }

  function bindCard(card) {
    if (records.has(card)) return false;
    var button = card.querySelector('.sa-pcard__add');
    records.set(card, {
      button: button,
      title: card.dataset.title || 'Product',
      originalLabel: button ? button.getAttribute('aria-label') || 'Add product to cart' : ''
    });
    boundCards.add(card);
    normalizeRating(card);
    syncPendingAccessibility(card);
    if (root.SAProductCardMedia && typeof root.SAProductCardMedia.bind === 'function') {
      root.SAProductCardMedia.bind(card);
    }
    return true;
  }

  function bind(scope) {
    if (stopped) return 0;
    var count = 0;
    collect(scope || document).forEach(function (card) {
      if (bindCard(card)) count += 1;
    });
    if (count > 0) syncCart();
    return count;
  }

  function destroyCard(card) {
    if (!records.has(card)) return false;
    records.delete(card);
    boundCards.delete(card);
    if (root.SAProductCardMedia && typeof root.SAProductCardMedia.destroy === 'function') {
      root.SAProductCardMedia.destroy(card);
    }
    return true;
  }

  function destroy(scope) {
    var count = 0;
    var wholeHost = !scope || scope === document;
    var cards = wholeHost ? Array.from(boundCards) : collect(scope);
    if (wholeHost) {
      stopped = true;
      if (observer) observer.disconnect();
      observer = null;
      if (cartListener && root.removeEventListener) {
        root.removeEventListener('sa:cart-updated', cartListener);
      }
      cartListener = null;
    }
    cards.forEach(function (card) {
      if (destroyCard(card)) count += 1;
    });
    return count;
  }

  function boot() {
    if (started || stopped || !document) return;
    started = true;
    cartListener = syncCart;
    if (root.addEventListener) root.addEventListener('sa:cart-updated', cartListener);
    bind(document);
    if (!root.MutationObserver || !document.documentElement) return;
    observer = new root.MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.removedNodes || [], function (node) {
          collect(node).forEach(function (card) {
            if (!card.isConnected) destroyCard(card);
          });
        });
      });
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes || [], bind);
        Array.prototype.forEach.call(mutation.addedNodes || [], function (node) {
          var card = closestCard(node);
          if (card && records.has(card)) normalizeRating(card);
        });
        if (mutation.type === 'attributes') {
          var card = closestCard(mutation.target);
          if (!card || !records.has(card)) return;
          if (mutation.attributeName === 'data-score' || mutation.attributeName === 'data-number-of-reviews') {
            normalizeRating(card);
          } else if (mutation.attributeName === 'class' && mutation.target.matches && mutation.target.matches('.sa-pcard__add')) {
            syncPendingAccessibility(card);
          }
        }
      });
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-score', 'data-number-of-reviews']
    });
  }

  if (document) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot, { once: true });
    } else {
      boot();
    }
  }

  return Object.freeze({ bind: bind, destroy: destroy });
}));
